import type { SectionKind } from "@/stores/sectionCompletionStore";
import { CourseTreeNodeTypeEnum, type CourseTreeNode } from "@/types/generated";

interface ChapterNavEntry {
  node: CourseTreeNode;
  chapter: CourseTreeNode | null;
}

type ChapterNavIndex = ReadonlyMap<string, ChapterNavEntry>;

const subchaptersOf = (chapter: CourseTreeNode): CourseTreeNode[] =>
  chapter.children?.filter(
    (child) => child.type === CourseTreeNodeTypeEnum.Subchapter
  ) ?? [];

/**
 * If `nodeId` is the first subchapter of a chapter that has intro text, returns
 * that chapter’s id so navigation can stay on the stacked intro route.
 */
const resolveFirstSubIntroChapterId = (
  nodeId: string,
  nodeIndex: ChapterNavIndex
): string | null => {
  const entry = nodeIndex.get(nodeId);
  if (!entry || entry.node.type !== CourseTreeNodeTypeEnum.Subchapter) {
    return null;
  }

  const chapter = entry.chapter;
  if (!chapter || !chapter.intro?.trim()) {
    return null;
  }

  const firstSubchapter = subchaptersOf(chapter)[0];
  if (firstSubchapter?.id !== nodeId) {
    return null;
  }

  return chapter.id;
};

/**
 * If `nodeId` is the last subchapter of a chapter that has outro text, returns
 * that chapter’s id so navigation can stay on the stacked outro content-entry route.
 */
const resolveLastSubOutroChapterId = (
  nodeId: string,
  nodeIndex: ChapterNavIndex
): string | null => {
  const entry = nodeIndex.get(nodeId);
  if (!entry || entry.node.type !== CourseTreeNodeTypeEnum.Subchapter) {
    return null;
  }

  const chapter = entry.chapter;
  if (!chapter || !chapter.outro?.trim()) {
    return null;
  }

  const subchapters = subchaptersOf(chapter);
  const lastSubchapter = subchapters[subchapters.length - 1];
  if (lastSubchapter?.id !== nodeId) {
    return null;
  }

  return chapter.id;
};

/**
 * Builds a query string for last-subchapter → stacked outro content entry:
 * `view=outro` plus `unveil=1` (content band active; distinct from TOC Abschluss).
 */
const buildOutroContentEntryQueryString = (
  queryParams: string | URLSearchParams
): string => {
  const params = new URLSearchParams(queryParams);
  params.set("view", "outro");
  params.set("unveil", "1");
  const query = params.toString();
  return query ? `?${query}` : "";
};

/**
 * Builds a query string that keeps existing params, drops outro view, and sets
 * the chapter-intro content unveil flag.
 */
const buildIntroUnveilQueryString = (
  queryParams: string | URLSearchParams
): string => {
  const params = new URLSearchParams(queryParams);
  params.delete("view");
  params.set("unveil", "1");
  const query = params.toString();
  return query ? `?${query}` : "";
};

/**
 * Drops course-nav mode flags (`unveil`, `view`) for plain content / intro
 * links, while preserving any other query params.
 */
const stripCourseNavQueryString = (
  queryParams: string | URLSearchParams
): string => {
  const params = new URLSearchParams(queryParams);
  params.delete("unveil");
  params.delete("view");
  const query = params.toString();
  return query ? `?${query}` : "";
};

/**
 * Query string for TOC Einführung / Abschluss links: drops `unveil` so those
 * entries land on the section band, and sets `view=outro` only for Abschluss.
 */
const buildSectionNavQueryString = (
  kind: SectionKind,
  queryParams: string | URLSearchParams
): string => {
  if (kind === "intro") return stripCourseNavQueryString(queryParams);

  const params = new URLSearchParams(queryParams);
  params.set("view", "outro");
  params.delete("unveil");
  const query = params.toString();
  return query ? `?${query}` : "";
};

/** True when `unveil=1` — content band of an intro/outro stack. */
const isUnveilQuery = (queryParams: URLSearchParams): boolean =>
  queryParams.get("unveil") === "1";

export {
  buildIntroUnveilQueryString,
  buildOutroContentEntryQueryString,
  buildSectionNavQueryString,
  isUnveilQuery,
  resolveFirstSubIntroChapterId,
  resolveLastSubOutroChapterId,
  stripCourseNavQueryString,
  subchaptersOf,
};
